import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type {
  OnCreateSkillSubscription,
  OnDeleteSkillSubscription,
  OnUpdateSkillSubscription,
} from "~/api/API";
import { SkillVisibility } from "~/api/API";
import { listSkills } from "~/api/graphql/queries";
import {
  onCreateSkill,
  onDeleteSkill,
  onUpdateSkill,
} from "~/api/graphql/subscriptions";
import {
  type AvailableSkillsCache,
  availableSkillsAtom,
  type SkillMenuItem,
} from "~/atoms/chatbot";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { errorReporter } from "~/error-handling/errorReporter";
import { useSubscriptions } from "~/hooks/useSubscriptions";
import { graphql } from "~/lib/graphql-client";
import { AuthUserGroup } from "~/types/auth";
import { actorCanInvoke } from "./actorCanInvoke";

/** Re-fetch skills after this many ms of cache age. Modal-open is the trigger. */
const TTL_MS = 60_000;

/** Cap on listSkills items returned per page. Mirrors the backend's "≤~1K skills
 *  org-wide" assumption (see services/agent_chat/skills.py:list_for_user).
 *  TODO: when volume crosses ~5K rows, switch to paginated fetch via nextToken
 *  in lockstep with the backend's planned move to a byVisibility GSI. */
const LIST_PAGE_LIMIT = 1000;

export interface UseAvailableSkillsResult {
  /** PUBLISHED, non-archived skills the user is allowed to invoke. Sorted alphabetically. */
  skills: SkillMenuItem[];
  /** Force re-fetch from GraphQL and update the cache. */
  refresh: () => Promise<void>;
  /** Re-fetch only if cache is missing or older than TTL. */
  refreshIfStale: () => Promise<void>;
}

export function useAvailableSkills(): UseAvailableSkillsResult {
  const [cache, setCache] = useAtom(availableSkillsAtom);
  const { userGroup } = useAuthSession();
  // Doctors can't read Skill rows through AppSync, and shouldn't: a row carries
  // the skill body. Their skills reach the model through the backend's
  // role-filtered skill tool, so the slash menu stays empty instead of erroring.
  const canListSkills = userGroup !== AuthUserGroup.DOCTOR;

  const refresh = useCallback(async () => {
    if (!canListSkills) return;
    // Failures here are non-fatal — the slash menu just stays empty (or
    // shows the previously cached list). Report and move on; don't bubble
    // an unhandled rejection out of the modal-open effect.
    try {
      const { data } = await graphql({
        query: listSkills,
        variables: { limit: LIST_PAGE_LIMIT },
      });
      const items: SkillMenuItem[] = (data?.listSkills?.items ?? []).filter(
        (s): s is SkillMenuItem => s != null,
      );
      setCache({
        skills: items,
        fetchedAt: Date.now(),
      });
    } catch (err) {
      errorReporter.reportMessage("Failed to fetch skills for slash menu", {
        level: "warning",
        tags: { feature: "skill_slash_menu" },
        extra: { error: err instanceof Error ? err.message : String(err) },
      });
    }
  }, [canListSkills, setCache]);

  // Keep a ref to the latest cache so `refreshIfStale` doesn't need to depend
  // on `cache` itself — otherwise every successful fetch invalidates the
  // callback and re-fires the consumer's mount effect for a no-op TTL guard.
  const cacheRef = useRef<AvailableSkillsCache | null>(cache);
  useEffect(() => {
    cacheRef.current = cache;
  }, [cache]);

  const refreshIfStale = useCallback(async () => {
    const c = cacheRef.current;
    const age = c ? Date.now() - c.fetchedAt : Infinity;
    if (age > TTL_MS) {
      await refresh();
    }
  }, [refresh]);

  // Live refresh on any skill mutation — covers portal authoring saves
  // (publish, archive, edit) and Indy's chat-side authoring alike. Without
  // this, the slash menu stays stale until the next modal-open TTL miss.
  // Cheap: each event triggers one `listSkills` page; the result overwrites
  // the cache atomically.
  //
  // Note: `useSubscriptions` gates its onUpdate behind a truthy data check
  // (see `if (data) { onUpdateRef.current(data); }`), so our transform must
  // return *something truthy*. We don't care about the payload — just the
  // fact that *some* skill changed — so we surface the raw subscription
  // result (always non-null when an event fires).
  const triggerRefresh = useCallback(() => {
    void refresh();
  }, [refresh]);

  useSubscriptions<OnCreateSkillSubscription, OnCreateSkillSubscription>({
    queries: canListSkills
      ? [
          {
            key: "chatbot.onCreateSkill",
            query: onCreateSkill,
            transform: (data) => data,
          },
        ]
      : [],
    onUpdate: triggerRefresh,
  });

  useSubscriptions<OnUpdateSkillSubscription, OnUpdateSkillSubscription>({
    queries: canListSkills
      ? [
          {
            key: "chatbot.onUpdateSkill",
            query: onUpdateSkill,
            transform: (data) => data,
          },
        ]
      : [],
    onUpdate: triggerRefresh,
  });

  useSubscriptions<OnDeleteSkillSubscription, OnDeleteSkillSubscription>({
    queries: canListSkills
      ? [
          {
            key: "chatbot.onDeleteSkill",
            query: onDeleteSkill,
            transform: (data) => data,
          },
        ]
      : [],
    onUpdate: triggerRefresh,
  });

  const skills = useMemo(() => {
    if (!cache || !canListSkills) return [];
    return cache.skills
      .filter(
        (s) =>
          s.visibility === SkillVisibility.PUBLISHED &&
          !s.archivedAt &&
          actorCanInvoke(s, userGroup),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cache, canListSkills, userGroup]);

  return { skills, refresh, refreshIfStale };
}
